<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useDebounce } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import type { FieldSchema } from '../../../types';
import { useSessionStore } from '@Stores';

export interface Model {
  weight: number;
  unit: string;
}

defineProps<{
  disabled?: boolean;
  error?: string;
  field?: FieldSchema;
  placeholder?: string;
  required?: boolean;
}>();

const { shelterProfile } = storeToRefs(useSessionStore());

const { t } = useI18n();

const model = defineModel<Model | string | undefined>();
const modelNumber = ref();
const unit = ref();

const options = computed(() => [
  `${t('weight.units.g')}`,
  `${t('weight.units.kg')}`,
  `${t('weight.units.lbs')}`,
  `${t('weight.units.oz')}`,
]);

const input = useDebounce(modelNumber, 400);

onMounted(() => setModelOnStart());

watch(input, updateModel);
watch(model, setModelOnStart);

function setModelOnStart() {
  unit.value = shelterProfile.value?.weight_unit;

  if (!model.value) return;

  if (typeof model.value === 'string') {
    const [weightStr, unitStr] = model.value.split(' ');
    model.value = { weight: Number(weightStr) || 0, unit: unitStr };
  }

  modelNumber.value = Number(model.value.weight) || 0;
  unit.value = model.value?.unit?.toLowerCase();
}

function updateModel() {
  if (modelNumber.value === undefined || !unit.value) return;

  if (modelNumber.value === 0 && unit.value) return (model.value = undefined);

  model.value = { weight: Number(modelNumber.value), unit: unit.value };
}
</script>

<template>
  <div class="flex flex-col gap-3 sm:flex-row">
    <div class="w-full sm:!w-3/4">
      <AFieldNumber
        v-model="modelNumber"
        :disabled="field?.state?.disabled || disabled"
        :error
        :maxFractionDigits="field?.settings.maxFractionDigits || 3"
        :min="field?.settings.min"
        :minFractionDigits="field?.settings.minFractionDigits || 2"
        :required
      />
    </div>

    <AFieldDropdown
      v-model="unit"
      class="w-full sm:!w-1/4"
      :disabled="field?.state?.disabled || disabled"
      :error
      :options
      :placeholder
      :required
      @change="updateModel"
    />
  </div>
</template>
